const WHITELIST_DOMAINS = ['nuclias.com'];
const SAFE_PROTOCOLS = ['http:', 'https:'];
const CENTRAL_CASDOOR_URL_BY_HOSTNAME = new Map([
    ['unity-rd2.nuclias.com', 'https://login-rd.unity.nuclias.com/aspmm'],
    ['unity-rd3.nuclias.com', 'https://login-rd.unity.nuclias.com/aspmm'],
    ['login-rd.unity.nuclias.com', 'https://login-rd.unity.nuclias.com/aspmm'],
    ['unity-qa2.nuclias.com', 'https://login-qa.unity.nuclias.com/aspmm'],
    ['unity-qa3.nuclias.com', 'https://login-qa.unity.nuclias.com/aspmm'],
    ['login-qa.unity.nuclias.com', 'https://login-qa.unity.nuclias.com/aspmm'],
    ['unity-eu.nuclias.com', 'https://login.unity.nuclias.com/aspmm'],
    ['unity-sg.nuclias.com', 'https://login.unity.nuclias.com/aspmm'],
    ['login.unity.nuclias.com', 'https://login.unity.nuclias.com/aspmm'],
    ['unity.nuclias.com', 'https://login.unity.nuclias.com/aspmm'],
]);

const isPremises = () => process.env.REACT_APP_RUNNING_CONTEXT === 'premises';
const isCloudLocal = () => process.env.REACT_APP_IS_CLOUD_LOCAL === "true";

const isWhitelistedDomain = (hostname) => {
    return WHITELIST_DOMAINS.some(domain =>
        hostname === domain || hostname.endsWith(`.${domain}`)
    );
};

const getSecureOrigin = () => {
    try {
        const currentUrl = new URL(window.location.origin);

        if (!SAFE_PROTOCOLS.includes(currentUrl.protocol)) {
            throw new Error(`not valid protocol`);
        }

        if (!isPremises() && !isCloudLocal() && !isWhitelistedDomain(currentUrl.hostname)) {
            throw new Error(`domain not valid`);
        }

        return currentUrl.origin;
    } catch (error) {
        throw error;
    }
};

const getSecureWebSocketURL = () => {
    try {
        const currentUrl = new URL(window.location.origin);

        if (!SAFE_PROTOCOLS.includes(currentUrl.protocol)) {
            throw new Error(`not valid protocol`);
        }

        if (!isPremises() && !isCloudLocal() && !isWhitelistedDomain(currentUrl.hostname)) {
            throw new Error(`domain not valid`);
        }

        const protocol = currentUrl.protocol === 'https:' ? 'wss:' : 'ws:';
        const hostname = currentUrl.hostname;
        const port = currentUrl.port ? `:${currentUrl.port}` : '';

        return `${protocol}//${hostname}${port}/ws/`;
    } catch (error) {
        throw error;
    }
};

const getServerURL = () => {
    const envServerURL = process.env.REACT_APP_SERVERURL;

    // 如果環境變數存在，直接使用（不進行檢查）
    if (envServerURL) {
        return envServerURL;
    }

    // 如果沒有環境變數，使用安全的 runtime 動態產生
    const secureOrigin = getSecureOrigin();
    return `${secureOrigin}/aspmm`;
};

const getCentralCasdoorURL = (origin) => {
    try {
        const url = new URL(origin);
        if (url.protocol !== 'https:') {
            return null;
        }

        const hostname = url.hostname.toLowerCase();
        return CENTRAL_CASDOOR_URL_BY_HOSTNAME.get(hostname) || null;
    } catch (error) {
        return null;
    }
};

const getCasdoorURL = () => {
    // Production uses the runtime frontend hostname. Local development may run
    // on localhost, so fall back to the configured regional server URL.
    let centralCasdoorURL = null;
    try {
        centralCasdoorURL = getCentralCasdoorURL(getSecureOrigin());
    } catch (error) {
        // getServerURL() below remains the fallback for local/premises builds.
    }

    if (!centralCasdoorURL) {
        centralCasdoorURL = getCentralCasdoorURL(getServerURL());
    }

    return centralCasdoorURL || getServerURL().replace(/\/+$/, '');
};

const getAppApiURL = () => {
    const envServerURL = process.env.REACT_APP_API_URL;

    // 如果環境變數存在，直接使用（不進行檢查）
    if (envServerURL) {
        return envServerURL;
    }

    // 如果沒有環境變數，使用安全的 runtime 動態產生
    return getSecureOrigin();
};

const getWebSocketURL = () => {
    const envWebSocketURL = process.env.REACT_APP_WEBSOCKET_URL;

    // 如果環境變數存在，直接使用（不進行檢查）
    if (envWebSocketURL) {
        return envWebSocketURL;
    }

    // 如果沒有環境變數，使用安全的 runtime 動態產生
    return getSecureWebSocketURL();
};

/**
 * Check server environment from server URL
 * - rd: *.unity-rd*.nuclias.com, unity-rd*.nuclias.com
 * - qa: *.unity-qa*.nuclias.com, unity-qa*.nuclias.com
 * - prod: *.unity.nuclias.com, unity.nuclias.com
 * @example
 * getServerEnvironment()
 * // returns { enum: { RD: 'rd', QA: 'qa', PROD: 'prod', UNKNOWN: 'unknown' }, environment: 'rd' }
 * @returns
 * { enum: { RD: string, QA: string, PROD: string, UNKNOWN: string }, environment: string, isRDSite: function, isQASite: function, isProdSite: function, isUnknownSite: function }
 */
const getServerEnvironment = () => {
    const env = {
        serverURL: '',
        enum: {
            RD: 'rd',
            QA: 'qa',
            PROD: 'prod',
            UNKNOWN: 'unknown',
        },
        environment: '',
        isRDSite: function () {
            return this.environment === this.enum.RD;
        },
        isQASite: function () {
            return this.environment === this.enum.QA;
        },
        isProdSite: function () {
            return this.environment === this.enum.PROD;
        },
        isUnknownSite: function () {
            return this.environment === this.enum.UNKNOWN;
        },
    };

    const serverURL = getServerURL();
    env.serverURL = serverURL;

    try {
        const url = new URL(serverURL);
        const hostname = url.hostname;

        if (/^(.+\.)?unity-rd\d*\.nuclias\.com$/.test(hostname)) {
            env.environment = env.enum.RD;
        } else if (/^(.+\.)?unity-qa\d*\.nuclias\.com$/.test(hostname)) {
            env.environment = env.enum.QA;
        } else if (/^(.+\.)?unity\.nuclias\.com$/.test(hostname)) {
            env.environment = env.enum.PROD;
        } else {
            env.environment = env.enum.UNKNOWN;
        }
    } catch (error) {
        env.environment = env.enum.UNKNOWN;
    }

    return env;
}

export {
    getServerURL,
    getCentralCasdoorURL,
    getCasdoorURL,
    getWebSocketURL,
    getAppApiURL,
    getServerEnvironment,
};
